import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const ScreenContainer = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px); /* Account for progress bar */
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  text-align: center;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;


const TextContent = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.body};
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 400px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${props => {
    if (props.$hasError) return theme.colors.error;
    if (props.$isValid) return theme.colors.success;
    return theme.colors.doryLighterBlue;
  }};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
  
  &::placeholder {
    color: ${theme.colors.textLight};
  }
`;

const AutocompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const AutocompleteDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-top: none;
  border-radius: 0 0 ${theme.borderRadius.md} ${theme.borderRadius.md};
  max-height: 200px;
  overflow-y: auto;
  z-index: ${theme.zIndex.dropdown};
  box-shadow: ${theme.shadows.md};
`;

const AutocompleteItem = styled.div`
  padding: ${theme.spacing.md};
  cursor: pointer;
  border-bottom: 1px solid ${theme.colors.doryBeige};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  
  &:hover {
    background: ${theme.colors.doryBeige};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.accent});
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  margin-top: ${theme.spacing.lg};
  width: 100%;
  align-self: center;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin-top: ${theme.spacing.sm};
  text-align: left;
  width: 100%;
`;

const PhoneFieldContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0px;
  width: 100%;
`;

const CountryCodeSelect = styled.select`
  padding: ${theme.spacing.inputPadding};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  background: white;
  transition: border-color ${theme.animation.durations.fast};
  min-width: 55px;
  width: 55px;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  flex-shrink: 0;
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

const PhoneInput = styled(Input)`
  width: calc(100% - 25px);
  margin-left: -10px;
  border-color: ${props => {
    if (props.$hasError) return theme.colors.error;
    if (props.$isValid) return theme.colors.success || '#28a745';
    return theme.colors.doryLighterBlue;
  }};
`;

const StatusIcon = styled.div`
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  font-weight: bold;
  color: ${props => {
    if (props.$hasError) return theme.colors.error;
    if (props.$isValid) return theme.colors.success || '#28a745';
    return 'transparent';
  }};
  transition: color ${theme.animation.durations.fast};
  pointer-events: none;
  z-index: 1;
`;

const SectionHeading = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin: ${theme.spacing.xl} 0 0 0;
  text-align: left;
  width: 100%;
  
  &:first-child {
    margin-top: 0;
  }
`;

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
  width: 100%;
`;

const MembershipEntryScreen = ({ data, onNext, userProgress = {} }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    countryCode: '+1',
    countryId: 'us',
    location: '',
    profession: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [professionSuggestions, setProfessionSuggestions] = useState([]);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [showProfessionDropdown, setShowProfessionDropdown] = useState(false);

  // Country codes data
  const countryCodes = [
    { id: 'us', code: '+1', country: 'United States', flag: '🇺🇸' },
    { id: 'ca', code: '+1', country: 'Canada', flag: '🇨🇦' },
    { id: 'gb', code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { id: 'au', code: '+61', country: 'Australia', flag: '🇦🇺' },
    { id: 'de', code: '+49', country: 'Germany', flag: '🇩🇪' },
    { id: 'fr', code: '+33', country: 'France', flag: '🇫🇷' },
    { id: 'it', code: '+39', country: 'Italy', flag: '🇮🇹' },
    { id: 'es', code: '+34', country: 'Spain', flag: '🇪🇸' },
    { id: 'nl', code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { id: 'se', code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { id: 'no', code: '+47', country: 'Norway', flag: '🇳🇴' },
    { id: 'dk', code: '+45', country: 'Denmark', flag: '🇩🇰' },
    { id: 'fi', code: '+358', country: 'Finland', flag: '🇫🇮' },
    { id: 'ch', code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { id: 'at', code: '+43', country: 'Austria', flag: '🇦🇹' },
    { id: 'be', code: '+32', country: 'Belgium', flag: '🇧🇪' },
    { id: 'pt', code: '+351', country: 'Portugal', flag: '🇵🇹' },
    { id: 'ie', code: '+353', country: 'Ireland', flag: '🇮🇪' },
    { id: 'pl', code: '+48', country: 'Poland', flag: '🇵🇱' },
    { id: 'cz', code: '+420', country: 'Czech Republic', flag: '🇨🇿' },
    { id: 'hu', code: '+36', country: 'Hungary', flag: '🇭🇺' },
    { id: 'sk', code: '+421', country: 'Slovakia', flag: '🇸🇰' },
    { id: 'si', code: '+386', country: 'Slovenia', flag: '🇸🇮' },
    { id: 'hr', code: '+385', country: 'Croatia', flag: '🇭🇷' },
    { id: 'bg', code: '+359', country: 'Bulgaria', flag: '🇧🇬' },
    { id: 'ro', code: '+40', country: 'Romania', flag: '🇷🇴' },
    { id: 'gr', code: '+30', country: 'Greece', flag: '🇬🇷' },
    { id: 'cy', code: '+357', country: 'Cyprus', flag: '🇨🇾' },
    { id: 'mt', code: '+356', country: 'Malta', flag: '🇲🇹' },
    { id: 'lu', code: '+352', country: 'Luxembourg', flag: '🇱🇺' },
    { id: 'ee', code: '+372', country: 'Estonia', flag: '🇪🇪' },
    { id: 'lv', code: '+371', country: 'Latvia', flag: '🇱🇻' },
    { id: 'lt', code: '+370', country: 'Lithuania', flag: '🇱🇹' },
    { id: 'jp', code: '+81', country: 'Japan', flag: '🇯🇵' },
    { id: 'kr', code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { id: 'cn', code: '+86', country: 'China', flag: '🇨🇳' },
    { id: 'in', code: '+91', country: 'India', flag: '🇮🇳' },
    { id: 'sg', code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { id: 'hk', code: '+852', country: 'Hong Kong', flag: '🇭🇰' },
    { id: 'tw', code: '+886', country: 'Taiwan', flag: '🇹🇼' },
    { id: 'th', code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { id: 'my', code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { id: 'id', code: '+62', country: 'Indonesia', flag: '🇮🇩' },
    { id: 'ph', code: '+63', country: 'Philippines', flag: '🇵🇭' },
    { id: 'vn', code: '+84', country: 'Vietnam', flag: '🇻🇳' },
    { id: 'br', code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { id: 'mx', code: '+52', country: 'Mexico', flag: '🇲🇽' },
    { id: 'ar', code: '+54', country: 'Argentina', flag: '🇦🇷' },
    { id: 'cl', code: '+56', country: 'Chile', flag: '🇨🇱' },
    { id: 'co', code: '+57', country: 'Colombia', flag: '🇨🇴' },
    { id: 'pe', code: '+51', country: 'Peru', flag: '🇵🇪' },
    { id: 've', code: '+58', country: 'Venezuela', flag: '🇻🇪' },
    { id: 'uy', code: '+598', country: 'Uruguay', flag: '🇺🇾' },
    { id: 'py', code: '+595', country: 'Paraguay', flag: '🇵🇾' },
    { id: 'bo', code: '+591', country: 'Bolivia', flag: '🇧🇴' },
    { id: 'ec', code: '+593', country: 'Ecuador', flag: '🇪🇨' },
    { id: 'za', code: '+27', country: 'South Africa', flag: '🇿🇦' },
    { id: 'ng', code: '+234', country: 'Nigeria', flag: '🇳🇬' },
    { id: 'ke', code: '+254', country: 'Kenya', flag: '🇰🇪' },
    { id: 'eg', code: '+20', country: 'Egypt', flag: '🇪🇬' },
    { id: 'ma', code: '+212', country: 'Morocco', flag: '🇲🇦' },
    { id: 'tn', code: '+216', country: 'Tunisia', flag: '🇹🇳' },
    { id: 'dz', code: '+213', country: 'Algeria', flag: '🇩🇿' },
    { id: 'ly', code: '+218', country: 'Libya', flag: '🇱🇾' },
    { id: 'sd', code: '+249', country: 'Sudan', flag: '🇸🇩' },
    { id: 'et', code: '+251', country: 'Ethiopia', flag: '🇪🇹' },
    { id: 'gh', code: '+233', country: 'Ghana', flag: '🇬🇭' },
    { id: 'ug', code: '+256', country: 'Uganda', flag: '🇺🇬' },
    { id: 'tz', code: '+255', country: 'Tanzania', flag: '🇹🇿' },
    { id: 'zw', code: '+263', country: 'Zimbabwe', flag: '🇿🇼' },
    { id: 'bw', code: '+267', country: 'Botswana', flag: '🇧🇼' },
    { id: 'na', code: '+264', country: 'Namibia', flag: '🇳🇦' },
    { id: 'sz', code: '+268', country: 'Eswatini', flag: '🇸🇿' },
    { id: 'ls', code: '+266', country: 'Lesotho', flag: '🇱🇸' },
    { id: 'mw', code: '+265', country: 'Malawi', flag: '🇲🇼' },
    { id: 'zm', code: '+260', country: 'Zambia', flag: '🇿🇲' },
    { id: 'ao', code: '+244', country: 'Angola', flag: '🇦🇴' },
    { id: 'mz', code: '+258', country: 'Mozambique', flag: '🇲🇿' },
    { id: 'mg', code: '+261', country: 'Madagascar', flag: '🇲🇬' },
    { id: 'mu', code: '+230', country: 'Mauritius', flag: '🇲🇺' },
    { id: 'sc', code: '+248', country: 'Seychelles', flag: '🇸🇨' },
    { id: 're', code: '+262', country: 'Réunion', flag: '🇷🇪' },
    { id: 'yt', code: '+262', country: 'Mayotte', flag: '🇾🇹' },
    { id: 'km', code: '+269', country: 'Comoros', flag: '🇰🇲' },
    { id: 'dj', code: '+253', country: 'Djibouti', flag: '🇩🇯' },
    { id: 'so', code: '+252', country: 'Somalia', flag: '🇸🇴' },
    { id: 'er', code: '+291', country: 'Eritrea', flag: '🇪🇷' },
    { id: 'ss', code: '+211', country: 'South Sudan', flag: '🇸🇸' },
    { id: 'cf', code: '+236', country: 'Central African Republic', flag: '🇨🇫' },
    { id: 'td', code: '+235', country: 'Chad', flag: '🇹🇩' },
    { id: 'ne', code: '+227', country: 'Niger', flag: '🇳🇪' },
    { id: 'ml', code: '+223', country: 'Mali', flag: '🇲🇱' },
    { id: 'bf', code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
    { id: 'ci', code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮' },
    { id: 'gn', code: '+224', country: 'Guinea', flag: '🇬🇳' },
    { id: 'sl', code: '+232', country: 'Sierra Leone', flag: '🇸🇱' },
    { id: 'lr', code: '+231', country: 'Liberia', flag: '🇱🇷' },
    { id: 'sn', code: '+221', country: 'Senegal', flag: '🇸🇳' },
    { id: 'gm', code: '+220', country: 'Gambia', flag: '🇬🇲' },
    { id: 'gw', code: '+245', country: 'Guinea-Bissau', flag: '🇬🇼' },
    { id: 'cv', code: '+238', country: 'Cape Verde', flag: '🇨🇻' },
    { id: 'st', code: '+239', country: 'São Tomé and Príncipe', flag: '🇸🇹' },
    { id: 'gq', code: '+240', country: 'Equatorial Guinea', flag: '🇬🇶' },
    { id: 'ga', code: '+241', country: 'Gabon', flag: '🇬🇦' },
    { id: 'cg', code: '+242', country: 'Republic of the Congo', flag: '🇨🇬' },
    { id: 'cd', code: '+243', country: 'Democratic Republic of the Congo', flag: '🇨🇩' },
    { id: 'cm', code: '+237', country: 'Cameroon', flag: '🇨🇲' },
    { id: 'td', code: '+235', country: 'Chad', flag: '🇹🇩' },
    { id: 'ne', code: '+227', country: 'Niger', flag: '🇳🇪' },
    { id: 'ml', code: '+223', country: 'Mali', flag: '🇲🇱' },
    { id: 'bf', code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
    { id: 'ci', code: '+225', country: 'Côte d\'Ivoire', flag: '🇨🇮' },
    { id: 'gn', code: '+224', country: 'Guinea', flag: '🇬🇳' },
    { id: 'sl', code: '+232', country: 'Sierra Leone', flag: '🇸🇱' },
    { id: 'lr', code: '+231', country: 'Liberia', flag: '🇱🇷' },
    { id: 'sn', code: '+221', country: 'Senegal', flag: '🇸🇳' },
    { id: 'gm', code: '+220', country: 'Gambia', flag: '🇬🇲' },
    { id: 'gw', code: '+245', country: 'Guinea-Bissau', flag: '🇬🇼' },
    { id: 'cv', code: '+238', country: 'Cape Verde', flag: '🇨🇻' },
    { id: 'st', code: '+239', country: 'São Tomé and Príncipe', flag: '🇸🇹' },
    { id: 'gq', code: '+240', country: 'Equatorial Guinea', flag: '🇬🇶' },
    { id: 'ga', code: '+241', country: 'Gabon', flag: '🇬🇦' },
    { id: 'cg', code: '+242', country: 'Republic of the Congo', flag: '🇨🇬' },
    { id: 'cd', code: '+243', country: 'Democratic Republic of the Congo', flag: '🇨🇩' },
    { id: 'cm', code: '+237', country: 'Cameroon', flag: '🇨🇲' },
    { id: 'ru', code: '+7', country: 'Russia', flag: '🇷🇺' },
    { id: 'ua', code: '+380', country: 'Ukraine', flag: '🇺🇦' },
    { id: 'by', code: '+375', country: 'Belarus', flag: '🇧🇾' },
    { id: 'md', code: '+373', country: 'Moldova', flag: '🇲🇩' },
    { id: 'ge', code: '+995', country: 'Georgia', flag: '🇬🇪' },
    { id: 'am', code: '+374', country: 'Armenia', flag: '🇦🇲' },
    { id: 'az', code: '+994', country: 'Azerbaijan', flag: '🇦🇿' },
    { id: 'kz', code: '+7', country: 'Kazakhstan', flag: '🇰🇿' },
    { id: 'uz', code: '+998', country: 'Uzbekistan', flag: '🇺🇿' },
    { id: 'tm', code: '+993', country: 'Turkmenistan', flag: '🇹🇲' },
    { id: 'tj', code: '+992', country: 'Tajikistan', flag: '🇹🇯' },
    { id: 'kg', code: '+996', country: 'Kyrgyzstan', flag: '🇰🇬' },
    { id: 'mn', code: '+976', country: 'Mongolia', flag: '🇲🇳' },
    { id: 'af', code: '+93', country: 'Afghanistan', flag: '🇦🇫' },
    { id: 'pk', code: '+92', country: 'Pakistan', flag: '🇵🇰' },
    { id: 'bd', code: '+880', country: 'Bangladesh', flag: '🇧🇩' },
    { id: 'lk', code: '+94', country: 'Sri Lanka', flag: '🇱🇰' },
    { id: 'mv', code: '+960', country: 'Maldives', flag: '🇲🇻' },
    { id: 'bt', code: '+975', country: 'Bhutan', flag: '🇧🇹' },
    { id: 'np', code: '+977', country: 'Nepal', flag: '🇳🇵' },
    { id: 'mm', code: '+95', country: 'Myanmar', flag: '🇲🇲' },
    { id: 'la', code: '+856', country: 'Laos', flag: '🇱🇦' },
    { id: 'kh', code: '+855', country: 'Cambodia', flag: '🇰🇭' },
    { id: 'bn', code: '+673', country: 'Brunei', flag: '🇧🇳' },
    { id: 'tl', code: '+670', country: 'East Timor', flag: '🇹🇱' },
    { id: 'fj', code: '+679', country: 'Fiji', flag: '🇫🇯' },
    { id: 'pg', code: '+675', country: 'Papua New Guinea', flag: '🇵🇬' },
    { id: 'sb', code: '+677', country: 'Solomon Islands', flag: '🇸🇧' },
    { id: 'vu', code: '+678', country: 'Vanuatu', flag: '🇻🇺' },
    { id: 'nc', code: '+687', country: 'New Caledonia', flag: '🇳🇨' },
    { id: 'pf', code: '+689', country: 'French Polynesia', flag: '🇵🇫' },
    { id: 'ws', code: '+685', country: 'Samoa', flag: '🇼🇸' },
    { id: 'to', code: '+676', country: 'Tonga', flag: '🇹🇴' },
    { id: 'ki', code: '+686', country: 'Kiribati', flag: '🇰🇮' },
    { id: 'tv', code: '+688', country: 'Tuvalu', flag: '🇹🇻' },
    { id: 'nr', code: '+674', country: 'Nauru', flag: '🇳🇷' },
    { id: 'pw', code: '+680', country: 'Palau', flag: '🇵🇼' },
    { id: 'fm', code: '+691', country: 'Micronesia', flag: '🇫🇲' },
    { id: 'mh', code: '+692', country: 'Marshall Islands', flag: '🇲🇭' },
    { id: 'as', code: '+1', country: 'American Samoa', flag: '🇦🇸' },
    { id: 'gu', code: '+1', country: 'Guam', flag: '🇬🇺' },
    { id: 'mp', code: '+1', country: 'Northern Mariana Islands', flag: '🇲🇵' },
    { id: 'vi', code: '+1', country: 'U.S. Virgin Islands', flag: '🇻🇮' },
    { id: 'pr', code: '+1', country: 'Puerto Rico', flag: '🇵🇷' },
    { id: 'do', code: '+1', country: 'Dominican Republic', flag: '🇩🇴' },
    { id: 'ht', code: '+509', country: 'Haiti', flag: '🇭🇹' },
    { id: 'cu', code: '+53', country: 'Cuba', flag: '🇨🇺' },
    { id: 'jm', code: '+1', country: 'Jamaica', flag: '🇯🇲' },
    { id: 'tt', code: '+1', country: 'Trinidad and Tobago', flag: '🇹🇹' },
    { id: 'bb', code: '+1', country: 'Barbados', flag: '🇧🇧' },
    { id: 'lc', code: '+1', country: 'Saint Lucia', flag: '🇱🇨' },
    { id: 'vc', code: '+1', country: 'Saint Vincent and the Grenadines', flag: '🇻🇨' },
    { id: 'gd', code: '+1', country: 'Grenada', flag: '🇬🇩' },
    { id: 'ag', code: '+1', country: 'Antigua and Barbuda', flag: '🇦🇬' },
    { id: 'kn', code: '+1', country: 'Saint Kitts and Nevis', flag: '🇰🇳' },
    { id: 'dm', code: '+1', country: 'Dominica', flag: '🇩🇲' },
    { id: 'bs', code: '+1', country: 'Bahamas', flag: '🇧🇸' },
    { id: 'bz', code: '+501', country: 'Belize', flag: '🇧🇿' },
    { id: 'gt', code: '+502', country: 'Guatemala', flag: '🇬🇹' },
    { id: 'sv', code: '+503', country: 'El Salvador', flag: '🇸🇻' },
    { id: 'hn', code: '+504', country: 'Honduras', flag: '🇭🇳' },
    { id: 'ni', code: '+505', country: 'Nicaragua', flag: '🇳🇮' },
    { id: 'cr', code: '+506', country: 'Costa Rica', flag: '🇨🇷' },
    { id: 'pa', code: '+507', country: 'Panama', flag: '🇵🇦' },
    { id: 'aw', code: '+297', country: 'Aruba', flag: '🇦🇼' },
    { id: 'an', code: '+599', country: 'Netherlands Antilles', flag: '🇦🇳' },
    { id: 'cw', code: '+599', country: 'Curaçao', flag: '🇨🇼' },
    { id: 'sx', code: '+599', country: 'Sint Maarten', flag: '🇸🇽' },
    { id: 'bq', code: '+599', country: 'Bonaire', flag: '🇧🇶' },
    { id: 'sr', code: '+597', country: 'Suriname', flag: '🇸🇷' },
    { id: 'gy', code: '+592', country: 'Guyana', flag: '🇬🇾' },
    { id: 'fk', code: '+500', country: 'Falkland Islands', flag: '🇫🇰' },
    { id: 'gs', code: '+500', country: 'South Georgia and the South Sandwich Islands', flag: '🇬🇸' },
    { id: 'sh', code: '+290', country: 'Saint Helena', flag: '🇸🇭' },
    { id: 'ac', code: '+247', country: 'Ascension Island', flag: '🇦🇨' },
    { id: 'ta', code: '+290', country: 'Tristan da Cunha', flag: '🇹🇦' },
    { id: 'io', code: '+246', country: 'British Indian Ocean Territory', flag: '🇮🇴' },
    { id: 'cx', code: '+61', country: 'Christmas Island', flag: '🇨🇽' },
    { id: 'cc', code: '+61', country: 'Cocos Islands', flag: '🇨🇨' },
    { id: 'nf', code: '+672', country: 'Norfolk Island', flag: '🇳🇫' },
    { id: 'hm', code: '+672', country: 'Heard Island and McDonald Islands', flag: '🇭🇲' },
    { id: 'aq', code: '+672', country: 'Antarctica', flag: '🇦🇶' },
    { id: 'tf', code: '+262', country: 'French Southern and Antarctic Lands', flag: '🇹🇫' },
    { id: 'bv', code: '+47', country: 'Bouvet Island', flag: '🇧🇻' },
    { id: 'sj', code: '+47', country: 'Svalbard and Jan Mayen', flag: '🇸🇯' },
    { id: 'gl', code: '+299', country: 'Greenland', flag: '🇬🇱' },
    { id: 'fo', code: '+298', country: 'Faroe Islands', flag: '🇫🇴' },
    { id: 'ax', code: '+358', country: 'Åland Islands', flag: '🇦🇽' },
    { id: 'je', code: '+44', country: 'Jersey', flag: '🇯🇪' },
    { id: 'gg', code: '+44', country: 'Guernsey', flag: '🇬🇬' },
    { id: 'im', code: '+44', country: 'Isle of Man', flag: '🇮🇲' },
    { id: 'gi', code: '+350', country: 'Gibraltar', flag: '🇬🇮' },
    { id: 'ad', code: '+376', country: 'Andorra', flag: '🇦🇩' },
    { id: 'sm', code: '+378', country: 'San Marino', flag: '🇸🇲' },
    { id: 'va', code: '+379', country: 'Vatican City', flag: '🇻🇦' },
    { id: 'mc', code: '+377', country: 'Monaco', flag: '🇲🇨' },
    { id: 'li', code: '+423', country: 'Liechtenstein', flag: '🇱🇮' },
    { id: 'xk', code: '+383', country: 'Kosovo', flag: '🇽🇰' },
    { id: 'me', code: '+382', country: 'Montenegro', flag: '🇲🇪' },
    { id: 'rs', code: '+381', country: 'Serbia', flag: '🇷🇸' },
    { id: 'ba', code: '+387', country: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    { id: 'mk', code: '+389', country: 'North Macedonia', flag: '🇲🇰' },
    { id: 'al', code: '+355', country: 'Albania', flag: '🇦🇱' },
    { id: 'xk', code: '+383', country: 'Kosovo', flag: '🇽🇰' },
    { id: 'me', code: '+382', country: 'Montenegro', flag: '🇲🇪' },
    { id: 'rs', code: '+381', country: 'Serbia', flag: '🇷🇸' },
    { id: 'ba', code: '+387', country: 'Bosnia and Herzegovina', flag: '🇧🇦' },
    { id: 'mk', code: '+389', country: 'North Macedonia', flag: '🇲🇰' },
    { id: 'al', code: '+355', country: 'Albania', flag: '🇦🇱' }
  ];

  // Get formatted HTML content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Helper function to extract country code from phone number (for backward compatibility)
  const extractCountryCodeFromPhone = (phoneNumber) => {
    if (!phoneNumber) return { countryCode: '+1', phoneNumber: '' };
    
    // Remove all non-digit characters except + for detection
    const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');
    
    // If phone doesn't start with +, assume no country code
    if (!cleanPhone.startsWith('+')) {
      return { countryCode: '+1', phoneNumber: cleanPhone };
    }
    
    // Try to find matching country code by checking all country codes
    // Sort by length (longest first) to match longer codes before shorter ones
    const sortedCountryCodes = [...countryCodes].sort((a, b) => b.code.length - a.code.length);
    
    for (const country of sortedCountryCodes) {
      if (cleanPhone.startsWith(country.code)) {
        // Extract phone number without country code
        const phoneWithoutCode = cleanPhone.substring(country.code.length);
        return { 
          countryCode: country.code, 
          phoneNumber: phoneWithoutCode.replace(/\D/g, '') 
        };
      }
    }
    
    // If no country code was found, try to match any country code pattern (1-4 digits after +)
    const match = cleanPhone.match(/^\+(\d{1,4})/);
    if (match) {
      const potentialCode = '+' + match[1];
      const foundCountry = countryCodes.find(code => code.code === potentialCode);
      if (foundCountry) {
        const phoneWithoutCode = cleanPhone.substring(potentialCode.length);
        return { 
          countryCode: foundCountry.code, 
          phoneNumber: phoneWithoutCode.replace(/\D/g, '') 
        };
      }
    }
    
    // Fallback: no country code detected, return as-is
    return { countryCode: '+1', phoneNumber: cleanPhone.replace(/\D/g, '') };
  };

  // Load existing user data
  useEffect(() => {
    if (userProgress.userInfo) {
      // Read phone and countryCode separately from userInfo
      let phoneNumber = userProgress.userInfo.phone || '';
      let countryCode = userProgress.userInfo.countryCode;
      let countryId = 'us';
      
      // Backward compatibility: if countryCode doesn't exist but phone starts with +, extract it
      if (!countryCode && phoneNumber && (phoneNumber.startsWith('+') || phoneNumber.match(/^\d/))) {
        const extracted = extractCountryCodeFromPhone(phoneNumber);
        countryCode = extracted.countryCode;
        phoneNumber = extracted.phoneNumber;
      } else {
        // Use stored countryCode or default to +1
        countryCode = countryCode || '+1';
        // Remove any non-digit characters from phone number for the input field
        phoneNumber = phoneNumber.replace(/\D/g, '');
      }
      
      // Find the country ID based on the country code
      const selectedCountry = countryCodes.find(code => code.code === countryCode);
      if (selectedCountry) {
        countryId = selectedCountry.id;
      }
      
      setFormData({
        fullName: userProgress.userInfo.name || '',
        email: userProgress.userInfo.email || '',
        phone: phoneNumber,
        countryCode: countryCode,
        countryId: countryId,
        location: userProgress.userInfo.location || '',
        profession: userProgress.userInfo.profession || ''
      });
    }
  }, [userProgress.userInfo]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone, countryCode = '+1') => {
    if (!phone || !phone.trim()) return false;
    
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Check if it's a valid length (7-15 digits for international numbers)
    if (digitsOnly.length < 7 || digitsOnly.length > 15) return false;
    
    // Basic format check - should contain at least some digits
    if (digitsOnly.length === 0) return false;
    
    return true;
  };

  const formatPhoneNumber = (phone, countryCode = '+1') => {
    if (!phone) return '';
    
    // Remove all non-digit characters
    const digits = phone.replace(/\D/g, '');
    
    // Format based on country code
    switch (countryCode) {
      case '+1': // US/Canada
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      
      case '+44': // UK
        if (digits.length <= 4) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
        return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
      
      case '+49': // Germany
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
      
      case '+33': // France
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
        return `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
      
      case '+81': // Japan
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      
      case '+86': // China
        if (digits.length <= 3) return digits;
        if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7, 11)}`;
      
      case '+91': // India
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
      
      case '+61': // Australia
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
      
      default:
        // Generic international formatting
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)}`;
    }
  };

  const handlePhoneInputChange = (e) => {
    const input = e.target.value;
    const digits = input.replace(/\D/g, '');
    
    // Only update if we have digits or if user is clearing the field
    if (digits.length > 0 || input === '') {
      const formatted = formatPhoneNumber(digits, formData.countryCode);
      handleInputChange('phone', formatted);
      
      // Clear any previous phone validation errors
      if (errors.phone) {
        setErrors(prev => ({ ...prev, phone: '' }));
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLocationChange = (value) => {
    handleInputChange('location', value);
    setShowLocationDropdown(true);
    
    if (value.length > 2) {
      // Simulate location search - in real implementation, this would call an API
      const mockLocations = [
        'New York, United States',
        'London, United Kingdom',
        'Tokyo, Japan',
        'Paris, France',
        'Sydney, Australia',
        'Toronto, Canada',
        'Berlin, Germany',
        'Mumbai, India',
        'São Paulo, Brazil',
        'Mexico City, Mexico'
      ];
      
      const filtered = mockLocations.filter(loc => 
        loc.toLowerCase().includes(value.toLowerCase())
      );
      setLocationSuggestions(filtered);
    } else {
      setLocationSuggestions([]);
    }
  };

  const handleProfessionChange = (value) => {
    handleInputChange('profession', value);
    setShowProfessionDropdown(true);
    
    if (value.length > 1) {
      // Simulate profession search - in real implementation, this would call an API
      const mockProfessions = [
        'Software Engineer',
        'Data Scientist',
        'Product Manager',
        'Marketing Manager',
        'Sales Representative',
        'Financial Analyst',
        'Graphic Designer',
        'Project Manager',
        'Business Analyst',
        'UX Designer',
        'Accountant',
        'Consultant',
        'Teacher',
        'Doctor',
        'Lawyer',
        'Architect',
        'Engineer',
        'Nurse',
        'Chef',
        'Writer'
      ];
      
      const filtered = mockProfessions.filter(prof => 
        prof.toLowerCase().includes(value.toLowerCase())
      );
      setProfessionSuggestions(filtered);
    } else {
      setProfessionSuggestions([]);
    }
  };

  const selectLocation = (location) => {
    setFormData(prev => ({ ...prev, location }));
    setShowLocationDropdown(false);
  };

  const selectProfession = (profession) => {
    setFormData(prev => ({ ...prev, profession }));
    setShowProfessionDropdown(false);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhoneNumber(formData.phone, formData.countryCode)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.profession.trim()) {
      newErrors.profession = 'Unique Abilities is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Call onNext with the form data to save to Firebase
      // Store phone and countryCode separately to avoid parsing issues
      await onNext(data.key, {
        type: 'membership_entry',
        formData: {
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone.replace(/\D/g, ''), // Store only digits, no country code
          countryCode: formData.countryCode, // Store country code separately
          location: formData.location,
          profession: formData.profession
        }
      });
    } catch (error) {
      console.error('Error submitting membership entry:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScreenContainer>
      {/* Display text content if provided */}
      {htmlContent && (
        <TextContent dangerouslySetInnerHTML={{ __html: htmlContent }} />
      )}
      
      <FormContainer>
        <Form onSubmit={handleSubmit}>
          {/* Public Fields Section */}
          <SectionContainer>
            <SectionHeading>Public Fields</SectionHeading>
            
            {/* Full Name */}
            <Input
              type="text"
              placeholder="Full Name"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName', e.target.value)}
              $hasError={!!errors.fullName}
              $isValid={formData.fullName && !errors.fullName}
            />
            {errors.fullName && <ErrorMessage>{errors.fullName}</ErrorMessage>}
            
            {/* Location */}
            <AutocompleteContainer>
              <Input
                type="text"
                placeholder="Location (City, Country)"
                value={formData.location}
                onChange={(e) => handleLocationChange(e.target.value)}
                onFocus={() => setShowLocationDropdown(formData.location.length > 2)}
                onBlur={() => setTimeout(() => setShowLocationDropdown(false), 200)}
                $hasError={!!errors.location}
                $isValid={formData.location && !errors.location}
              />
              {showLocationDropdown && locationSuggestions.length > 0 && (
                <AutocompleteDropdown>
                  {locationSuggestions.map((location, index) => (
                    <AutocompleteItem
                      key={index}
                      onClick={() => selectLocation(location)}
                    >
                      {location}
                    </AutocompleteItem>
                  ))}
                </AutocompleteDropdown>
              )}
            </AutocompleteContainer>
            {errors.location && <ErrorMessage>{errors.location}</ErrorMessage>}
            
            {/* Unique Abilities */}
            <Input
              type="text"
              placeholder="Unique Abilities"
              value={formData.profession}
              onChange={(e) => {
                const value = e.target.value;
                if (value.length <= 50) {
                  handleInputChange('profession', value);
                }
              }}
              maxLength={50}
              $hasError={!!errors.profession}
              $isValid={formData.profession && !errors.profession}
            />
            <div style={{
              fontSize: theme.typography.sizes.small,
              color: theme.colors.textLight,
              marginTop: theme.spacing.xs,
              fontStyle: 'italic'
            }}>
              Examples: World Record Holder, Outdoor Adventurer, Author, Ski Guide, Rare Disease Researcher, Pharma Executive
            </div>
            {errors.profession && <ErrorMessage>{errors.profession}</ErrorMessage>}
          </SectionContainer>

          {/* Private Fields Section */}
          <SectionContainer>
            <SectionHeading>Private Fields</SectionHeading>
            
            {/* Email */}
            <Input
              type="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              $hasError={!!errors.email}
              $isValid={formData.email && validateEmail(formData.email)}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
            
            {/* Phone */}
            <PhoneFieldContainer>
              <CountryCodeSelect
                value={formData.countryId}
                onChange={(e) => {
                  const selectedCountry = countryCodes.find(code => code.id === e.target.value);
                  if (selectedCountry) {
                    handleInputChange('countryCode', selectedCountry.code);
                    handleInputChange('countryId', selectedCountry.id);
                  }
                }}
              >
                {countryCodes.map(code => (
                  <option key={code.id} value={code.id}>{code.code}</option>
                ))}
              </CountryCodeSelect>
              
              <div style={{ position: 'relative', flex: 1, marginLeft: 0 }}>
                <PhoneInput
                  id="phone-input"
                  type="tel"
                  autoComplete="tel"
                  placeholder="Enter your phone number"
                  value={formData.phone || ''}
                  onChange={handlePhoneInputChange}
                  $hasError={!!errors.phone}
                  $isValid={formData.phone && validatePhoneNumber(formData.phone, formData.countryCode)}
                />
                
                <StatusIcon
                  $hasError={!!errors.phone}
                  $isValid={formData.phone && validatePhoneNumber(formData.phone, formData.countryCode)}
                >
                  {errors.phone ? '×' : (formData.phone && validatePhoneNumber(formData.phone, formData.countryCode)) ? '✓' : ''}
                </StatusIcon>
              </div>
            </PhoneFieldContainer>
            
            {/* Phone validation hint */}
            {formData.phone && !validatePhoneNumber(formData.phone, formData.countryCode) && (
              <div style={{
                color: theme.colors.error,
                fontSize: theme.typography.sizes.small,
                marginTop: theme.spacing.xs,
                fontStyle: 'italic'
              }}>
                Please enter a valid phone number (7-15 digits)
              </div>
            )}
            {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
          </SectionContainer>
          
          <SubmitButton type="submit" disabled={loading}>
            {loading ? 'Saving...' : (data.content?.ctaText || 'Continue')}
          </SubmitButton>
        </Form>
      </FormContainer>
    </ScreenContainer>
  );
};

export default MembershipEntryScreen;
